import { Injectable, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { CrmPermissionStateService } from '../pages/auth/services/crm-permission-state.service';
import { moduleKeyForRoutePath } from './route-permission-map';

/**
 * PreloadAllModules with one exception: a feature module the signed-in user has
 * no permission for is not downloaded.
 *
 * The CRM ships ~140 lazy chunks. Preloading all of them makes navigation feel
 * instant, which is worth keeping — but it also means every user pulls down
 * AR Management, Exam Zone, CPD and the rest whether or not their role can open
 * a single page in them.
 *
 * The bias here is deliberately toward preloading:
 *
 *   - a route with no known permission module preloads (unchanged behaviour)
 *   - a route whose module the user *does* have access to preloads
 *   - permissions not loaded yet? preload — never withhold on missing data
 *
 * so the only thing ever skipped is a module the permission tree positively says
 * the user cannot use. Nothing here can block a navigation: skipping preload
 * only means the router fetches that chunk when the user actually goes there.
 */
@Injectable({ providedIn: 'root' })
export class PermissionAwarePreloadStrategy implements PreloadingStrategy {
  private readonly permissionState = inject(CrmPermissionStateService);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return this.shouldPreload(route) ? load() : of(null);
  }

  private shouldPreload(route: Route): boolean {
    const moduleKey = moduleKeyForRoutePath(route.path);

    // Not a permission-gated feature route (the shell itself, auth pages,
    // onboarding, anything not yet in the map) — behave as before.
    if (!moduleKey) {
      return true;
    }

    // Permissions arrive with the 2FA response and are re-read by the shell, but
    // preloading can start before either has landed. An empty tree means "not
    // known yet", not "no access", so preload rather than withhold.
    if (!this.permissionState.getAllPermissions()) {
      return true;
    }

    return this.permissionState.hasSupportedInModule(moduleKey);
  }
}
